import classNames from 'classnames';
import { CSSProperties, HTMLAttributes, ImgHTMLAttributes } from 'react';
import {
  ThemeIconKey,
  lineThemeIconAssetUrl,
  legacyPathToThemeIconKey,
  themeIconAssetUrl,
  themeIconUsesPng,
} from 'lib/themeIcons';
import { readAppliedPlayerTheme } from 'lib/playerTheme';
import {
  IconPathOptions,
  ThemeIconVariant,
  ThemeIconTone,
  defaultThemeIconTone,
  resolveThemeIconKey,
  themeIconClassName,
} from './iconUtils';
import styles from './ThemeIcon.module.css';

export type { ThemeIconKey, ThemeIconVariant, ThemeIconTone, IconPathOptions };

/**
 * A 1x1 transparent SVG. Alt themes draw the glyph with a CSS mask and paint it
 * with `currentColor`, so the element itself carries no pixels — but it stays an
 * <img> because dozens of CSS modules size their icons with `... > img` rules.
 */
const BLANK_PIXEL =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1' height='1'/%3E";

type ThemeIconProps = {
  icon: ThemeIconKey;
  alt?: string;
  variant?: ThemeIconVariant;
  tone?: ThemeIconTone;
  className?: string;
  active?: boolean;
  accent?: boolean;
  width?: number | string;
  height?: number | string;
  style?: CSSProperties;
} & Omit<
  HTMLAttributes<HTMLElement>,
  'children' | 'width' | 'height' | 'style'
>;

export function ThemeIcon({
  icon,
  alt = '',
  variant = 'inline',
  tone: toneProp,
  className,
  active,
  accent,
  width,
  height,
  style,
  onClick,
  onKeyDown,
  ...rest
}: ThemeIconProps) {
  const theme = readAppliedPlayerTheme();
  const tone = toneProp ?? defaultThemeIconTone(variant);
  const options: IconPathOptions = { active, accent };
  const resolvedIcon = resolveThemeIconKey(icon, options);
  const usesPng = themeIconUsesPng(theme);

  const maskUrl = usesPng
    ? undefined
    : `url('${lineThemeIconAssetUrl(resolvedIcon)}')`;

  return (
    <img
      src={usesPng ? themeIconAssetUrl(resolvedIcon) : BLANK_PIXEL}
      alt={alt}
      width={width}
      height={height}
      style={
        maskUrl
          ? ({
              ...style,
              WebkitMaskImage: maskUrl,
              maskImage: maskUrl,
            } as CSSProperties)
          : style
      }
      className={classNames(
        styles.icon,
        !usesPng && styles.lineIcon,
        themeIconClassName(variant, styles),
        className
      )}
      data-theme-icon=""
      data-icon-tone={usesPng ? undefined : tone}
      data-active={active ? '' : undefined}
      onClick={onClick}
      onKeyDown={onKeyDown}
      {...(rest as ImgHTMLAttributes<HTMLImageElement>)}
    />
  );
}

type ThemeIconFromSrcProps = {
  src: string;
  alt?: string;
  variant?: ThemeIconVariant;
  tone?: ThemeIconTone;
  className?: string;
  active?: boolean;
  accent?: boolean;
};

export function ThemeIconFromSrc({
  src,
  alt,
  variant = 'inline',
  tone,
  className,
  active,
  accent,
}: ThemeIconFromSrcProps) {
  const key = legacyPathToThemeIconKey(src);
  if (key) {
    return (
      <ThemeIcon
        icon={key}
        alt={alt}
        variant={variant}
        tone={tone}
        className={className}
        active={active}
        accent={accent}
      />
    );
  }
  return (
    <img src={src} alt={alt ?? ''} className={className} data-theme-icon="" />
  );
}

export { ThemeIcon as ThemeMenuIcon };
